import { clientEnv } from '@pricedrop/config/client';
import { useRouter } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { authClient, useSession } from '@/lib/auth-client';

export function GoogleOneTap() {
  const { data: session, isPending } = useSession();
  const router = useRouter();
  const triggeredRef = useRef(false);
  const isAuthenticated = !!session?.user;

  useEffect(() => {
    // Skip if One Tap is not configured, user is authenticated, or already triggered
    if (!clientEnv.GOOGLE_CLIENT_ID || isPending || isAuthenticated || triggeredRef.current) {
      return;
    }

    triggeredRef.current = true;

    authClient.oneTap({
      fetchOptions: {
        onSuccess: () => {
          // Remove Google's injected DOM elements BEFORE re-rendering.
          // GIS leaves iframes and containers that expand the <html> to
          // ~10000px width in RTL mode. Must happen synchronously before
          // session/router updates trigger React re-renders.
          for (const id of [
            'credential_picker_container',
            'credential_picker_iframe',
            'g_a11y_announcement',
          ]) {
            document.getElementById(id)?.remove();
          }
          // Reset scroll in case RTL overflow already shifted the viewport
          window.scrollTo({ left: 0 });

          // Toggle the session signal atom so useSession() refetches and
          // re-renders across all components. The one-tap plugin doesn't
          // register atomListeners, so this manual notify is required.
          authClient.$store.notify('$sessionSignal');
          router.invalidate();
        },
        onError: (ctx) => {
          console.error('[google-one-tap] sign-in error:', ctx.error);
        },
      },
      onPromptNotification: (notification) => {
        console.debug('[google-one-tap] prompt notification:', notification);
      },
    });

    // No cleanup — calling google.accounts.id.cancel() here causes an abort
    // loop with React StrictMode's double-fire in dev. The One Tap prompt
    // dismisses itself when the user interacts or navigates away.
  }, [isPending, isAuthenticated, router]);

  return null;
}
